export type DiscountType = 'USD' | 'PERCENT';

/**
 * - `per-unit` — `$` value is per unit (e.g. $5 off each of 10 units = $50 line discount).
 *   Used when the discount lives on a reusable resource and the consuming invoice's
 *   quantity is not known at the time the discount is set.
 * - `per-line` — `$` value is the total dollar discount applied to the whole line as-is.
 *   Used in the invoice-line discount modal where the quantity for that invoice is fixed.
 *
 * `PERCENT` produces the same result under either scope because the math reduces to the
 * same expression, so callers may safely default to `per-unit`.
 */
export type DiscountScope = 'per-unit' | 'per-line';

export interface DiscountInput {
  type: DiscountType | null;
  value: number;
}

export interface DiscountComputation {
  /** Per-unit dollar discount derived from the (type, value) pair. */
  perUnitDiscount: number;
  /** Total dollar discount for the line, clamped to gross. */
  discountAmount: number;
  /** Gross line total = unitPrice × quantity. */
  grossTotal: number;
  /** Line total after the discount. */
  netTotal: number;
}

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Single source of truth for line discount math. Both `LineItemFormModal`
 * (per-source) and `InvoiceLineDiscountModal` (per-invoice-line) route through
 * this helper so rounding, clamping and PERCENT-vs-USD semantics never drift.
 */
export const computePerLineDiscount = (
  unitPrice: number,
  quantity: number,
  type: DiscountType | null | undefined,
  value: number | null | undefined,
  scope: DiscountScope = 'per-unit',
): DiscountComputation => {
  const qty = Number(quantity) || 0;
  const unit = Number(unitPrice) || 0;
  const grossTotal = round2(unit * qty);

  const numericValue = Number(value ?? 0);
  const active = !!type && numericValue > 0;
  if (!active) {
    return { perUnitDiscount: 0, discountAmount: 0, grossTotal, netTotal: grossTotal };
  }

  let rawAmount: number;
  if (scope === 'per-line') {
    // `$` is the dollar discount applied directly to the whole line.
    // `%` is a percentage of the gross line total.
    rawAmount = type === 'USD' ? numericValue : (grossTotal * numericValue) / 100;
  } else {
    // `$` is dollars off each unit, multiplied out by quantity.
    // `%` is the percentage off the unit price, then multiplied by quantity
    // (mathematically equivalent to `%` on grossTotal).
    const perUnit = type === 'USD' ? numericValue : (unit * numericValue) / 100;
    rawAmount = perUnit * qty;
  }

  // Discount never pushes a line below zero.
  const discountAmount = Math.min(grossTotal, round2(rawAmount));
  const perUnitDiscount = qty === 0 ? 0 : round2(discountAmount / qty);
  return {
    perUnitDiscount,
    discountAmount,
    grossTotal,
    netTotal: round2(grossTotal - discountAmount),
  };
};

/**
 * Helper for `per-unit` rehydration: given a persisted total discount amount
 * and the quantity, return the per-unit USD value the toggle can put back in
 * the input field. Not used for `per-line` scope (the persisted amount maps
 * 1:1 to the input value in that case).
 */
export const usdPerUnitFromAmount = (
  discountAmount: number,
  quantity: number,
): number => {
  const qty = Number(quantity) || 0;
  if (qty === 0) return 0;
  return round2(Number(discountAmount) / qty);
};
